const API_BASE = import.meta.env.VITE_INVESTIGATII_API_BASE || "http://localhost:3001/api";
const STORAGE_KEY = "investigatii_tokens";
const USER_KEY = "investigatii_user";

function getTokens() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function setTokens(tokens) {
  if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(STORAGE_KEY);
}

function getCachedUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function setCachedUser(user) {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

let onSessionExpired = () => {};
function setSessionExpiredHandler(fn) {
  onSessionExpired = fn;
}

async function request(path, options = {}, allowRefresh = true) {
  const tokens = getTokens();
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401 && allowRefresh && tokens?.refreshToken) {
    const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });
    if (refreshRes.ok) {
      const data = await refreshRes.json();
      setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      return request(path, options, false);
    }
    setTokens(null);
    setCachedUser(null);
    onSessionExpired();
    throw new Error("Sesiune expirată — autentifică-te din nou.");
  }

  if (!res.ok) {
    let message = `Eroare ${res.status}`;
    try {
      const body = await res.json();
      message = Array.isArray(body.message) ? body.message.join(", ") : body.message || message;
    } catch {
      // ignore — keep default message
    }
    throw new Error(message);
  }

  // NestJS DELETE handlers return 200 with an empty body unless the
  // controller sets @HttpCode(204) explicitly — most in this app don't, so
  // res.json() on an empty body would throw "Unexpected end of JSON input"
  // and abort the caller's post-delete refresh. Parse only if there's
  // actually something to parse, regardless of status code.
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const api = {
  async login(email, password) {
    const data = await request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }, false);
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setCachedUser(data.user);
    return data.user;
  },
  // Înregistrare self-service: creează organizația + primul cont (Owner) +
  // registrele implicite, apoi loghează direct — nu există verificare de
  // email în acest ecosistem.
  async register(organizationName, fullName, email, password) {
    const data = await request("/auth/register", { method: "POST", body: JSON.stringify({ organizationName, fullName, email, password }) }, false);
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setCachedUser(data.user);
    return data.user;
  },
  async logout() {
    const tokens = getTokens();
    setTokens(null);
    setCachedUser(null);
    if (tokens?.refreshToken) {
      await request("/auth/logout", { method: "POST", body: JSON.stringify({ refreshToken: tokens.refreshToken }) }, false).catch(() => {});
    }
  },
  // Auto-serviciu: contul logat își schimbă singur email-ul și/sau parola.
  // Backend-ul revocă sesiunile vechi, dar întoarce imediat o pereche nouă
  // de tokenuri pentru sesiunea curentă (același format ca login), ca
  // schimbarea să nu deconecteze silențios utilizatorul din propriul ecran.
  async updateAccount(currentPassword, newEmail, newPassword) {
    const body = { currentPassword };
    if (newEmail) body.newEmail = newEmail;
    if (newPassword) body.newPassword = newPassword;
    const data = await request("/auth/me", { method: "PATCH", body: JSON.stringify(body) });
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setCachedUser(data.user);
    return data.user;
  },
  isAuthenticated: () => !!getTokens()?.accessToken,
  getCachedUser,
  setSessionExpiredHandler,
  get: (path) => request(path),
  post: (path, body) => request(path, { method: "POST", body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: (path) => request(path, { method: "DELETE" }),
  // Upload-ul e multipart (fișier binar) — nu poate trece prin request(), care
  // serializează mereu body-ul ca JSON. FormData primește propriul
  // Content-Type (cu boundary), setat automat de browser — nu-l suprascriem.
  async upload(path, file, extraFields = {}) {
    const tokens = getTokens();
    const headers = tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {};
    const fd = new FormData();
    Object.entries(extraFields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    fd.append("file", file);
    const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: fd });
    if (!res.ok) {
      let message = `Eroare ${res.status}`;
      try {
        const body = await res.json();
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message || message;
      } catch {
        // ignore — keep default message
      }
      throw new Error(message);
    }
    return res.json();
  },
  // Descărcare autentificată (Authorization e un header, nu un cookie — un
  // <a href> simplu n-ar putea trimite tokenul) — fetch manual ca blob, apoi
  // declanșăm salvarea prin un link temporar, ca la un download normal.
  async download(path, filenameFallback) {
    const tokens = getTokens();
    const headers = tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {};
    const res = await fetch(`${API_BASE}${path}`, { headers });
    if (!res.ok) throw new Error(`Nu s-a putut descărca fișierul (${res.status}).`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filenameFallback || "fisier";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
